import type { FC } from 'react'

import { EmployerAgreementIdProvider } from '../../context/EmployerAgreementIdProvider'
import { useHook } from '../../lenteHooks'
import { sortEmployerAgreementsByStatus } from '../companyOverview/agreements/utility'
import { EmployerAgreementEventSelectionProvider } from '../employerAgreementEvents/EmployerAgreementEventSelectionProvider'

type Props<A = object> = {
  NestedCommand: FC<A & { employerAgreementId: string }>
  nestedProps: A
}

/**
 * Utility component to help nest commands that require an `employerAgreementId`.
 *
 * Mounts a single child when `employerAgreementId` is in the URL, and 0..N when not.
 * */
export const EmployerAgreementNester = <A = NonNullable<object>,>({
  NestedCommand,
  nestedProps,
}: Props<A>) => {
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { employerAgreementId, companyId } = useHook(
    'routing.employer.useEmployerAgreementParams',
  )({ isOptional: true })

  const { data: employerAgreementStatusesResponse } = useHook(
    'fetchers.life.useFetchEmployerAgreementEntitiesWithStatus',
  )(companyId ? { tenantId, companyId } : null)

  return employerAgreementId ? (
    <NestedCommand {...nestedProps} employerAgreementId={employerAgreementId} />
  ) : (
    sortEmployerAgreementsByStatus(
      employerAgreementStatusesResponse?.employerAgreements,
    ).map(({ employerAgreementId }) =>
      employerAgreementId ? (
        <EmployerAgreementIdProvider
          key={employerAgreementId}
          employerAgreementId={employerAgreementId}
        >
          <EmployerAgreementEventSelectionProvider>
            <NestedCommand
              key={employerAgreementId}
              {...nestedProps}
              employerAgreementId={employerAgreementId}
            />
          </EmployerAgreementEventSelectionProvider>
        </EmployerAgreementIdProvider>
      ) : null,
    )
  )
}
